import { useDealership } from '@/contexts/DealershipContext'
import {
  Conversation,
  conversationFetchOptions,
  getConversation,
  Message,
  pauseCustomerCommunication,
  restartCustomerCommunication,
  sendMessage,
  stopCustomerCommunication,
} from '@/services/conversations.api'
import { Dealership } from '@/types'
import { hasHtmlTags, sanitizeHtml } from '@/utils/sanitizeHtml'
import { downloadTranscript } from '@/utils/transcriptExport'
import { formatSystemEscalationTooltip, getTransferredStopDisplay } from '@/utils/conversation-stop-labels'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconDownload,
  IconMail,
  IconMessage,
  IconMessageOff,
  IconPhone,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
  IconRobot,
  IconSend,
  IconUserCheck,
  IconUserCircle,
} from '@tabler/icons-react'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { ConversationSummaryModal } from './ConversationSummaryModal'
import { VehicleInterestBadge } from './VehicleInterestBadge'

interface ConversationDetailProps {
  conversation: Conversation
  onUpdate: (updatedConversation?: Conversation) => void
  openSummary?: boolean
  onCloseSummary?: () => void
  isEvalCustomer?: boolean // Whether this customer is from an evaluation run
}

export function ConversationDetail({
  conversation,
  onUpdate,
  openSummary,
  onCloseSummary,
  isEvalCustomer = false,
}: ConversationDetailProps) {
  // Sending and simulating go through POST /workflows/process, which the API allows for super admins only.
  const { selectedEntity, isSuperUser: canSimulate } = useDealership()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')

  // Initialize simulation mode from localStorage
  const getSimulationModeFromStorage = () => {
    if (!canSimulate) {
      return false
    }
    try {
      const stored = localStorage.getItem(`simulationMode_${conversation.id}`)
      return stored === 'true'
    } catch {
      return false
    }
  }

  // Initialize hasControl from localStorage or conversation state
  const getHasControlFromStorage = () => {
    try {
      const stored = localStorage.getItem(`hasControl_${conversation.id}`)
      // Only use localStorage if it exists - otherwise default to AI control (false)
      if (stored !== null) {
        return stored === 'true'
      }
      // If no localStorage, use conversation state: if AI is active, user doesn't have control
      return conversation.aiActive !== undefined ? !conversation.aiActive : false
    } catch {
      // Default to AI control on error
      return false
    }
  }

  const [hasControl, setHasControl] = useState(getHasControlFromStorage())
  const [isSimulationMode, setIsSimulationMode] = useState(getSimulationModeFromStorage())
  const [isLifecycleActionLoading, setIsLifecycleActionLoading] = useState(false)
  const [summaryModalOpen, setSummaryModalOpen] = useState(false)
  const [summaryData, setSummaryData] = useState<any>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAttemptsRef = useRef(0)

  // Clear poll timer only when switching to a different conversation (or unmount).
  // Do not clear when only conversation.aiActive/status changes (e.g. after restart), so polling for new messages continues.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [conversation.id])

  useEffect(() => {
    if (conversation.messages?.length) {
      setMessages(conversation.messages)
    } else {
      setMessages([])
    }
    loadMessages()

    // Update simulation mode when conversation changes
    const storedSimulation = getSimulationModeFromStorage()
    setIsSimulationMode(storedSimulation)

    // Sync hasControl with conversation.aiActive when conversation loads
    // Only update if localStorage doesn't have a value, to preserve user's manual control
    const storedControl = localStorage.getItem(`hasControl_${conversation.id}`)
    if (storedControl === null && conversation.aiActive !== undefined) {
      // No localStorage - sync with conversation state
      setHasControl(!conversation.aiActive)
      // Also save to localStorage to prevent random popups on refresh
      try {
        localStorage.setItem(`hasControl_${conversation.id}`, String(!conversation.aiActive))
      } catch (error) {
        console.error('Failed to save hasControl to localStorage:', error)
      }
    } else if (storedControl !== null) {
      // Use stored value
      setHasControl(storedControl === 'true')
    }
  }, [conversation.id, conversation.aiActive])

  useEffect(() => {
    // Scroll to bottom when messages change, using requestAnimationFrame
    // to ensure the DOM has been painted with the new content first
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    })
  }, [messages])

  // Handle opening summary from parent
  useEffect(() => {
    if (openSummary) {
      handleShowSummary()
    }
  }, [openSummary])

  // Background polling: passively listens for new messages from the server.
  // Does not block user input — the user can keep sending messages while this runs.
  // Calling startPolling() while already polling resets the attempt counter,
  // extending the window so long-debounce scenarios don't time out.
  const startPolling = () => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') return

    // Reset attempts so each new message extends the polling window
    pollAttemptsRef.current = 0

    // If already polling, no need to start another loop
    if (pollTimerRef.current) return

    const dealership = selectedEntity as Dealership
    const maxAttempts = 30
    const pollIntervalMs = 2000

    const check = async () => {
      pollAttemptsRef.current++
      try {
        const conv = await getConversation(dealership.id, conversation.id, conversationFetchOptions(conversation))
        const newMessages = conv.messages || []
        let hasNewMessages = false
        // Only update state if messages actually changed to avoid unnecessary re-renders
        setMessages(prev => {
          if (
            prev.length === newMessages.length &&
            prev.length > 0 &&
            prev[prev.length - 1]?.id === newMessages[newMessages.length - 1]?.id
          ) {
            return prev // Same reference = no re-render
          }
          hasNewMessages = true
          return newMessages
        })

        // When new messages arrive, silently update the parent conversation list
        // so conversation-level fields (workflowStep, status) get updated.
        // Pass the already-fetched conv to avoid an extra API call.
        // Also reset the attempt counter — the conversation is still active,
        // so keep polling until 60s of *no changes*, not 60s from send.
        if (hasNewMessages) {
          pollAttemptsRef.current = 0
          onUpdate(conv)
        }
      } catch (error) {
        console.error('Polling failed:', error)
      }

      if (pollAttemptsRef.current >= maxAttempts) {
        pollTimerRef.current = null
        return
      }

      pollTimerRef.current = setTimeout(check, pollIntervalMs)
    }

    pollTimerRef.current = setTimeout(check, pollIntervalMs)
  }

  const loadMessages = async (): Promise<Message[]> => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') return []

    try {
      const dealership = selectedEntity as Dealership
      const conv = await getConversation(dealership.id, conversation.id, conversationFetchOptions(conversation))
      const messages = conv.messages || []
      setMessages(messages)
      return messages
    } catch (error) {
      console.error('Failed to load messages:', error)
      setMessages([])
      return []
    }
  }

  const handleLifecycleAction = async (action: 'pause' | 'stop' | 'restart') => {
    if (!selectedEntity || selectedEntity.type !== 'dealership' || !conversation.customerId) return

    const dealership = selectedEntity as Dealership
    setIsLifecycleActionLoading(true)

    const apiCall =
      action === 'pause'
        ? pauseCustomerCommunication
        : action === 'stop'
          ? stopCustomerCommunication
          : restartCustomerCommunication

    const actionLabels = { pause: 'Paused', stop: 'Stopped', restart: 'Restarted' }

    try {
      await apiCall(dealership.id, conversation.customerId)
    } catch (error: any) {
      const raw =
        error?.response?.data?.message || error?.message || `The server could not ${action} communication. Please try again.`
      const isUgly =
        raw.length > 120 || /\.(ts|js):\d+/.test(raw) || /ConnectorError|PrismaClient|invocation|QueryError/.test(raw)
      const message = isUgly ? `Failed to ${action} communication. Please try again.` : raw
      notifications.show({
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} failed`,
        message,
        color: 'red',
        autoClose: 8000,
      })
      setIsLifecycleActionLoading(false)
      return
    }

    let attempts = 0
    const maxAttempts = 15
    const pollInterval = 1000

    const checkUpdated = async (): Promise<boolean> => {
      try {
        const conv = await getConversation(dealership.id, conversation.id, conversationFetchOptions(conversation))
        const statusChanged = conv.status !== conversation.status
        if (statusChanged) {
          setMessages(conv.messages || [])
          if (action === 'stop') {
            setHasControl(true)
            try {
              localStorage.setItem(`hasControl_${conversation.id}`, 'true')
            } catch (e) {
              console.error('Failed to save hasControl to localStorage:', e)
            }
          } else if (action === 'restart') {
            setHasControl(false)
            try {
              localStorage.setItem(`hasControl_${conversation.id}`, 'false')
            } catch (e) {
              console.error('Failed to save hasControl to localStorage:', e)
            }
            // Re-engagement message is generated async by batch processor; poll for new messages like after send
            startPolling()
          }
          onUpdate(conv)
          notifications.show({
            title: `Communication ${actionLabels[action]}`,
            message: action === 'stop' ? 'A summary will be generated.' : undefined,
            color: 'blue',
          })
          if (action === 'stop' && conv.conversationSummary) {
            handleShowSummary()
          }
          return true
        }
        onUpdate(conv)
        return false
      } catch (error) {
        console.error('Failed to check conversation:', error)
        return false
      }
    }

    try {
      while (attempts < maxAttempts) {
        const done = await checkUpdated()
        if (done) break
        attempts++
        if (attempts < maxAttempts) await new Promise(r => setTimeout(r, pollInterval))
      }

      if (attempts >= maxAttempts && action === 'stop') {
        notifications.show({
          title: 'Summary Generating',
          message: 'The summary is still being generated. It will appear when ready.',
          color: 'yellow',
        })
      }
    } finally {
      setIsLifecycleActionLoading(false)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedEntity || selectedEntity.type !== 'dealership') return

    const messageContent = newMessage.trim()
    const dealership = selectedEntity as Dealership

    // Create temporary user message for immediate UI feedback
    const userMessage: Message = {
      id: `temp-user-${Date.now()}`,
      conversationId: conversation.id,
      role: isSimulationMode ? 'USER' : 'ASSISTANT',
      content: messageContent,
      channel: conversation.channel,
      direction: isSimulationMode ? 'inbound' : 'outbound',
      timestamp: new Date().toISOString(),
      metadata: {
        aiGenerated: false,
      },
    }

    setMessages(prev => [...prev, userMessage])
    setNewMessage('')

    if (isSimulationMode) {
      // In simulation mode, fire API call in the background so the user
      // can keep sending additional messages without waiting (debounce testing).
      // The input stays enabled — polling picks up AI responses asynchronously.
      sendMessage(dealership.id, conversation.id, messageContent, {
        simulateUser: true,
        channel: conversation.channel,
      })
        .then(async response => {
          if (!response.messages || response.messages.length === 0) {
            const messages = await loadMessages()
            const lastInbound = [...messages]
              .reverse()
              .find(m => m.direction === 'inbound' || m.role === 'USER')
            if (!lastInbound?.processedAt) {
              startPolling()
            }
            onUpdate()
          } else {
            loadMessages()
            onUpdate()
          }

          if (response.nextAction === 'HUMAN_HANDOFF' || response.updatedState?.workflowStep === 'HUMAN_HANDOFF') {
            notifications.show({
              title: 'Conversation Ready for Handoff',
              message: 'The AI conversation is complete.',
              color: 'green',
            })
            setTimeout(() => {
              handleShowSummary()
            }, 2000)
          }
        })
        .catch(() => {
          notifications.show({
            title: 'Error',
            message: 'Failed to send message',
            color: 'red',
          })
        })
    } else {
      // Non-simulation mode
      try {
        const response = await sendMessage(dealership.id, conversation.id, messageContent, {
          simulateUser: false,
          channel: conversation.channel,
        })

        await loadMessages()
        onUpdate()

        if (response.nextAction === 'HUMAN_HANDOFF' || response.updatedState?.workflowStep === 'HUMAN_HANDOFF') {
          notifications.show({
            title: 'Conversation Ready for Handoff',
            message: 'The AI conversation is complete.',
            color: 'green',
          })
          setTimeout(() => {
            handleShowSummary()
          }, 2000)
        }
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to send message',
          color: 'red',
        })
      }
    }
  }

  const toggleSimulationMode = () => {
    const newMode = !isSimulationMode
    setIsSimulationMode(newMode)

    // Persist to localStorage
    try {
      localStorage.setItem(`simulationMode_${conversation.id}`, String(newMode))
    } catch (error) {
      console.error('Failed to save simulation mode to localStorage:', error)
    }

    if (newMode) {
      notifications.show({
        title: 'User Simulation Mode Activated',
        message: 'You can now simulate customer messages to test the AI agent.',
        color: 'blue',
      })
    }
  }

  const handleShowSummary = async () => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') return

    setSummaryModalOpen(true)
    setLoadingSummary(true)

    try {
      const dealership = selectedEntity as Dealership
      const conv = await getConversation(dealership.id, conversation.id, conversationFetchOptions(conversation))

      // Extract summary data from the conversation
      setSummaryData({
        conversationSummary: conv.conversationSummary,
        workflowSummary: conv.workflowSummary,
        summaryGeneratedAt: conv.summaryGeneratedAt,
        summaryPostedToCrm: conv.summaryPostedToCrm,
        summaryPostedAt: conv.summaryPostedAt,
      })
    } catch (error) {
      console.error('Failed to load summary:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to load conversation summary',
        color: 'red',
      })
    } finally {
      setLoadingSummary(false)
    }
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Hide internal system messages (e.g. restart trigger) — they are not sent to the customer
  const visibleMessages = messages.filter(m => m.role !== 'SYSTEM' && m.metadata?.messageType !== 'SYSTEM_EVENT')

  const transferredDisplay =
    conversation.status === 'TRANSFERRED' ? getTransferredStopDisplay(conversation) : undefined

  const isReadOnly = conversation.status === 'PAUSED' || conversation.status === 'TRANSFERRED'
  const lifecycleButtonLabel =
    conversation.status === 'PAUSED'
      ? 'Paused'
      : conversation.status === 'TRANSFERRED'
        ? transferredDisplay?.badgeLabel ?? 'Stopped'
        : 'Pause / Stop / Restart'
  const readOnlyBorder =
    conversation.status === 'PAUSED'
      ? '2px solid var(--mantine-color-yellow-5)'
      : conversation.status === 'TRANSFERRED'
        ? `2px solid ${transferredDisplay?.borderColor ?? 'var(--mantine-color-red-5)'}`
        : 'none'

  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: isSimulationMode ? '2px solid var(--mantine-color-teal-6)' : readOnlyBorder,
      }}
    >
      {/* Header */}
      <Box
        p="md"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          flexShrink: 0,
        }}
      >
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group justify="space-between">
              <Group gap="xs">
                <Title order={3}>{conversation.customerName}</Title>
                {/* AI Customer badge - only shown for eval customers */}
                {isEvalCustomer && (
                  <Tooltip label="AI Customer (Evaluation)">
                    <Badge size="md" color="purple" variant="light" leftSection={<IconRobot size={14} />}>
                      AI Customer
                    </Badge>
                  </Tooltip>
                )}
                <Badge size="md" variant="filled" color="blue">
                  {conversation.channel}
                </Badge>
                {transferredDisplay && (
                  <Tooltip label={transferredDisplay.tooltip}>
                    <Badge
                      size="md"
                      variant="light"
                      color={transferredDisplay.badgeColor}
                      leftSection={<IconPlayerStop size={14} />}
                    >
                      {transferredDisplay.badgeLabel}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Group>

            {/* Contact Information */}
            <Group gap="sm">
              {conversation.customerPhone && (
                <Group gap={4}>
                  <IconPhone size={14} />
                  <Text size="sm" c="dimmed">
                    {conversation.customerPhone}
                  </Text>
                </Group>
              )}
              {conversation.customerEmail && (
                <Group gap={4}>
                  <IconMail size={14} />
                  <Text size="sm" c="dimmed">
                    {conversation.customerEmail}
                  </Text>
                </Group>
              )}
              <Text size="sm" c="dimmed">
                • {formatDuration(conversation.startedAt)}
              </Text>
            </Group>

            {/* Customer Tags */}
            <Flex gap="xs" wrap="wrap">
              {conversation.vehicleInterest && (
                <VehicleInterestBadge
                  label={conversation.vehicleInterest}
                  details={conversation.vehicleInterestDetails}
                  variant="badge"
                />
              )}
              {conversation.leadSource && (
                <Badge size="md" variant="light" color="violet">
                  {conversation.leadSource}
                </Badge>
              )}
            </Flex>
          </Stack>

          <Group gap="xs">
            <Tooltip label="Download Transcript">
              <ActionIcon
                size="lg"
                variant="subtle"
                color="gray"
                onClick={() => downloadTranscript(conversation, visibleMessages)}
              >
                <IconDownload size={20} />
              </ActionIcon>
            </Tooltip>
            {canSimulate && (
              <Tooltip label={isReadOnly ? 'User simulation is disabled when the conversation is paused or stopped.' : undefined}>
                <span>
                  <Button
                    color={isSimulationMode ? 'teal' : 'blue'}
                    variant={isSimulationMode ? 'filled' : 'outline'}
                    leftSection={<IconUserCircle size={18} />}
                    onClick={toggleSimulationMode}
                    disabled={isReadOnly}
                    styles={{
                      root: {
                        ...(isSimulationMode && !isReadOnly ? { color: 'white' } : {}),
                        ...(isSimulationMode && isReadOnly
                          ? {
                              '&:disabled, &[data-disabled]': {
                                opacity: 1,
                                backgroundColor: 'light-dark(var(--mantine-color-teal-2), var(--mantine-color-teal-8))',
                                color: 'light-dark(var(--mantine-color-teal-9), var(--mantine-color-white))',
                                border: 'none',
                              },
                            }
                          : {}),
                      },
                    }}
                  >
                    {isSimulationMode ? 'Exit Simulation' : 'User Simulation'}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Button
                  color={conversation.status === 'PAUSED' ? 'yellow' : 'red'}
                  variant="outline"
                  leftSection={<IconRobot size={18} />}
                  loading={isLifecycleActionLoading}
                  disabled={conversation.status === 'ACTIVE' && hasControl}
                >
                  {lifecycleButtonLabel}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPlayerPause size={16} />}
                  disabled={conversation.status !== 'ACTIVE'}
                  onClick={() => handleLifecycleAction('pause')}
                >
                  Pause
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPlayerPlay size={16} />}
                  disabled={conversation.status !== 'PAUSED'}
                  onClick={() => handleLifecycleAction('restart')}
                >
                  Restart
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPlayerStop size={16} />}
                  disabled={conversation.status !== 'ACTIVE' && conversation.status !== 'PAUSED'}
                  onClick={() => setStopConfirmOpen(true)}
                  color="red"
                >
                  Stop
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </Box>

      {/* Messages */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} p="md" viewportRef={scrollRef} type="auto" scrollbarSize={8}>
        <Box
          style={{
            maxWidth: conversation.channel === 'EMAIL' ? '768px' : '414px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          <Stack gap="md">
            {(() => {
              // Find the index of the last AI/assistant message so we can mark
              // all user messages after it as "pending" (awaiting AI response).
              const lastAiIndex = visibleMessages.reduce(
                (acc, msg, idx) => (msg.metadata?.aiGenerated || msg.role === 'ASSISTANT' ? idx : acc),
                -1,
              )
              return visibleMessages.map((message, idx) => {
                const isUserMsg = message.direction === 'inbound' || message.role === 'USER'
                const isPending = isSimulationMode && isUserMsg && idx > lastAiIndex && !message.processedAt
                return <MessageBubble key={message.id} message={message} formatTime={formatTime} isPending={isPending} />
              })
            })()}
            {(conversation.workflowStep === 'HUMAN_HANDOFF' || conversation.status === 'TRANSFERRED') && (
              <Group gap="xs" align="center" wrap="nowrap" style={{ margin: '8px 0' }}>
                <Box
                  style={{
                    flex: 1,
                    height: 1,
                    backgroundColor:
                      transferredDisplay?.badgeColor === 'orange'
                        ? 'var(--mantine-color-orange-4)'
                        : 'var(--mantine-color-green-4)',
                  }}
                />
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <IconUserCheck
                    size={14}
                    style={{
                      color:
                        transferredDisplay?.badgeColor === 'orange'
                          ? 'var(--mantine-color-orange-7)'
                          : 'var(--mantine-color-green-7)',
                    }}
                  />
                  <Text
                    size="xs"
                    fw={500}
                    c={transferredDisplay?.badgeColor === 'orange' ? 'orange.7' : 'green.7'}
                  >
                    {transferredDisplay?.handoffLabel ?? 'Handed off to human'}
                  </Text>
                </Group>
                <Box
                  style={{
                    flex: 1,
                    height: 1,
                    backgroundColor:
                      transferredDisplay?.badgeColor === 'orange'
                        ? 'var(--mantine-color-orange-4)'
                        : 'var(--mantine-color-green-4)',
                  }}
                />
              </Group>
            )}
            <div ref={messagesEndRef} />
          </Stack>
        </Box>
      </ScrollArea>

      {/* Bottom bar */}
      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: isSimulationMode ? 'rgba(20, 184, 166, 0.1)' : 'transparent',
          flexShrink: 0,
        }}
      >
        {/* Show message input only when in simulation mode and not paused */}
        {isSimulationMode && (
          <>
            {conversation.status === 'PAUSED' && (
              <Group gap="xs" mb="xs">
                <IconPlayerPause size={16} />
                <Text size="sm" c="dimmed">
                  Conversation paused. Use the Pause / Stop / Restart menu to Restart.
                </Text>
              </Group>
            )}
            {/* Message Input */}
            <Group gap="xs" align="flex-end">
              <Textarea
                style={{
                  flex: 1,
                  borderColor: isSimulationMode ? 'var(--mantine-color-teal-6)' : undefined,
                  borderWidth: isSimulationMode ? '2px' : undefined,
                }}
                placeholder={
                  conversation.status === 'PAUSED'
                    ? 'Paused — restart to send messages'
                    : 'Type a customer message to test the AI agent...'
                }
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                disabled={conversation.status === 'PAUSED'}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage()
                  }
                }}
                autosize
                minRows={isSimulationMode ? 2 : 1}
                maxRows={4}
              />
              <ActionIcon
                size="xl"
                color="teal"
                variant="filled"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || conversation.status === 'PAUSED'}
                style={{ minHeight: '42px', minWidth: '42px' }}
              >
                <IconSend size={20} />
              </ActionIcon>
            </Group>
          </>
        )}

        {/* Show AI control message when simulation mode is not active */}
        {!isSimulationMode && (
          <Group gap={4}>
            {isReadOnly ? (
              <>
                {conversation.status === 'PAUSED' ? <IconPlayerPause size={14} /> : <IconPlayerStop size={14} />}
                <Text size="sm" c="dimmed">
                  {conversation.status === 'PAUSED'
                    ? 'View only — conversation is paused. Use the menu above to Restart.'
                    : transferredDisplay?.readOnlyMessage ?? 'View only — conversation is stopped.'}
                </Text>
              </>
            ) : (
              <>
                <IconRobot size={14} />
                <Text size="sm" c="dimmed">
                  Use the Pause / Stop / Restart menu to control AI communication, or switch to User Simulation to test the AI.
                </Text>
              </>
            )}
          </Group>
        )}

        {isSimulationMode && (
          <Group gap={6} mt="xs">
            <Badge size="xs" color="teal" variant="filled" leftSection={<IconUserCircle size={10} />}>
              Simulation
            </Badge>
            <Text size="xs" c="dimmed">
              Press Enter to send • Send multiple messages in a row
            </Text>
          </Group>
        )}
      </Box>

      {/* Stop confirmation modal */}
      <Modal opened={stopConfirmOpen} onClose={() => setStopConfirmOpen(false)} title="Permanently stop conversation?" centered>
        <Text size="sm" c="dimmed" mb="md">
          This will permanently stop AI communication for this customer. A summary will be generated and the conversation cannot
          be resumed. This action cannot be undone.
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={() => setStopConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              setStopConfirmOpen(false)
              handleLifecycleAction('stop')
            }}
          >
            Permanently stop
          </Button>
        </Group>
      </Modal>

      {/* Summary Modal */}
      <ConversationSummaryModal
        opened={summaryModalOpen}
        onClose={() => {
          setSummaryModalOpen(false)
          onCloseSummary?.()
        }}
        summaryData={summaryData}
        loading={loadingSummary}
      />
    </Box>
  )
}

interface MessageBubbleProps {
  message: Message
  formatTime: (timestamp: string) => string
  isPending?: boolean
}

function MessageBubble({ message, formatTime, isPending = false }: MessageBubbleProps) {
  const isInbound = message.direction === 'inbound'
  const isSystemMessage = message.metadata?.systemGenerated === true
  const isStaff = message.metadata?.staffMessage === true
  const isAI = !isSystemMessage && !isStaff && (message.metadata?.aiGenerated || message.role === 'ASSISTANT')
  const isLoading = message.content === '...'
  const isEmail = message.channel === 'EMAIL'
  const isSms = message.channel === 'SMS'
  const isDeliveryBlocked = isSms && message.metadata?.deliveryBlocked === true

  const sanitizeUrl = (url: string): string | null => {
    try {
      const parsed = new URL(url)
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href
      }
      return null
    } catch {
      return null
    }
  }

  const renderContentWithLinks = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = content.split(urlRegex)

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        const safeUrl = sanitizeUrl(part)
        if (safeUrl) {
          return (
            <a
              key={index}
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: isEmail || isSystemMessage ? '#1c7ed6' : isAI || !isInbound ? 'white' : '#1c7ed6',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {part}
            </a>
          )
        }
      }
      return <span key={index}>{part}</span>
    })
  }

  // Determine background color
  let backgroundColor
  if (isSystemMessage) {
    // System-generated escalation messages: washed red
    backgroundColor = '#fee2e2'
  } else if (isAI) {
    // AI messages: lighter blue
    backgroundColor = '#3b82f6' // lighter blue for AI
  } else if (isStaff && !isInbound) {
    // Dealership staff to the customer, from the CRM: violet
    backgroundColor = 'var(--mantine-color-violet-6)'
  } else if (isInbound) {
    // Customer messages: light gray
    backgroundColor = 'var(--mantine-color-gray-1)'
  } else {
    // Agent messages: blue
    backgroundColor = 'var(--mantine-color-blue-6)'
  }

  const formatEmailTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Get channel icon
  const getChannelIcon = () => {
    if (message.channel === 'EMAIL') {
      return <IconMail size={14} />
    }
    if (message.channel === 'PHONE') {
      return <IconPhone size={14} />
    }
    return <IconMessage size={14} />
  }

  // Email-style layout
  if (isEmail) {
    return (
      <Paper
        withBorder
        p="md"
        radius="sm"
        style={{
          backgroundColor: 'white',
          width: '100%',
          marginBottom: '16px',
        }}
      >
        <Stack gap="xs">
          {/* Email Header */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              {isAI ? (
                <Badge size="md" color="blue" leftSection={getChannelIcon()}>
                  AI BDC
                </Badge>
              ) : isStaff && !isInbound ? (
                <Badge size="md" color="violet" variant="light" leftSection={getChannelIcon()}>
                  Dealer staff
                </Badge>
              ) : isInbound ? (
                <Badge size="md" color="orange" variant="light" leftSection={getChannelIcon()}>
                  {isStaff ? 'Customer to staff' : 'Customer'}
                </Badge>
              ) : (
                <Badge size="md" color="blue" variant="light" leftSection={getChannelIcon()}>
                  You
                </Badge>
              )}
              {message.metadata?.crmUpdated && (
                <Badge size="xs" color="green" variant="light">
                  CRM Updated
                </Badge>
              )}
              {message.metadata?.blockReason === 'CUSTOMER_OPTED_OUT' && (
                <Tooltip label="SMS not delivered: customer opted out">
                  <Badge size="xs" color="red" variant="light" leftSection={<IconMessageOff size={14} />}>
                    Unsubscribed
                  </Badge>
                </Tooltip>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {formatEmailTime(message.timestamp)}
            </Text>
          </Group>

          <Box style={{ borderTop: '1px solid var(--mantine-color-gray-3)', paddingTop: '12px' }}>
            {isLoading ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                  AI is composing response...
                </Text>
              </Group>
            ) : (
              <Stack gap="xs">
                {hasHtmlTags(message.content) ? (
                  <div
                    style={{ fontSize: '14px', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }}
                  />
                ) : (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {renderContentWithLinks(message.content)}
                  </Text>
                )}
                {isPending && <BouncingDots />}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    )
  }

  return (
    <Flex justify={isInbound ? 'flex-end' : 'flex-start'} align="flex-start" gap="xs">
      <Box
        style={{
          maxWidth: '85%',
          backgroundColor,
          color: isSystemMessage ? '#991b1b' : isAI || !isInbound ? 'white' : '#212121',
          borderRadius: 12,
          padding: '12px 16px',
          border: isSystemMessage
            ? '1px solid #fca5a5'
            : !isAI && isInbound
              ? '1px solid var(--mantine-color-gray-3)'
              : 'none',
        }}
      >
        <Stack gap="xs">
          <Group gap="xs" justify="space-between">
            <Group gap={4}>
              {isSystemMessage ? (
                <Tooltip label={formatSystemEscalationTooltip(message.metadata)}>
                  <Badge size="md" color="red" variant="light" leftSection={getChannelIcon()}>
                    SYSTEM Message
                  </Badge>
                </Tooltip>
              ) : isAI ? (
                <Tooltip label="AI Assistant">
                  <Badge size="md" color="green" leftSection={getChannelIcon()}>
                    AI BDC
                  </Badge>
                </Tooltip>
              ) : isStaff && !isInbound ? (
                <Badge size="md" color="violet" variant="filled" leftSection={getChannelIcon()}>
                  Dealer staff
                </Badge>
              ) : isInbound ? (
                <Badge size="md" color="orange" variant="filled" leftSection={getChannelIcon()}>
                  {isStaff ? 'Customer to staff' : 'Customer'}
                </Badge>
              ) : (
                <Badge size="md" color="blue" variant="filled" leftSection={getChannelIcon()}>
                  You
                </Badge>
              )}
              {message.metadata?.crmUpdated && (
                <Tooltip label="CRM has been updated">
                  <Badge size="md" color="green" variant="filled">
                    CRM Updated
                  </Badge>
                </Tooltip>
              )}
              {message.metadata?.blockReason === 'CUSTOMER_OPTED_OUT' && (
                <Tooltip label="SMS not delivered: customer opted out">
                  <Badge size="md" color="red" variant="filled" leftSection={<IconMessageOff size={14} />}>
                    Unsubscribed
                  </Badge>
                </Tooltip>
              )}
            </Group>
          </Group>
          {isLoading ? (
            <Group gap="xs">
              <Loader size="xs" color="white" />
              <Text size="sm" style={{ color: 'white', fontStyle: 'italic' }}>
                AI is typing...
              </Text>
            </Group>
          ) : (
            <>
              <Text
                size="sm"
                style={{
                  whiteSpace: 'pre-wrap',
                  color: isSystemMessage ? '#991b1b' : isAI || !isInbound ? 'white' : '#212121',
                }}
              >
                {renderContentWithLinks(message.content)}
              </Text>

              {isSms && message.mediaUrls && message.mediaUrls.length > 0 && (
                <Stack gap="xs" mt={4}>
                  {message.mediaUrls.map((url, idx) => {
                    const safe = sanitizeUrl(url)
                    if (!safe) return null
                    return (
                      <Box
                        key={`${message.id}-mms-${idx}`}
                        style={{ maxWidth: 280, borderRadius: 8, overflow: 'hidden' }}
                      >
                        <img
                          src={safe}
                          alt={`Attachment ${idx + 1}`}
                          style={{ width: '100%', height: 'auto', display: 'block', verticalAlign: 'middle' }}
                        />
                      </Box>
                    )
                  })}
                </Stack>
              )}

              <Group gap="xs" align="center">
                <Text
                  size="xs"
                  style={{
                    opacity: 0.7,
                    color: isSystemMessage ? '#b91c1c' : isAI || !isInbound ? 'white' : '#616161',
                  }}
                >
                  {formatTime(message.timestamp)}
                </Text>
                {isPending && <BouncingDots />}
                {isDeliveryBlocked && (
                  <Tooltip
                    label={
                      message.metadata?.blockReasonCode || message.metadata?.twilioErrorCode
                        ? `Code: ${message.metadata.blockReasonCode || message.metadata.twilioErrorCode}`
                        : undefined
                    }
                  >
                    <Badge size="xs" color={isAI || !isInbound ? 'yellow' : 'red'} variant="light" leftSection={<IconMessageOff size={12} />}>
                      Not delivered
                      {message.metadata?.blockReasonLabel ? ` (${message.metadata.blockReasonLabel})` : ''}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Box>
    </Flex>
  )
}

const bouncingDotsKeyframes = `
@keyframes bounceDot {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}
`

function BouncingDots() {
  const dotStyle = (delay: number): CSSProperties => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#f97316',
    display: 'inline-block',
    animation: 'bounceDot 1.4s infinite ease-in-out both',
    animationDelay: `${delay}s`,
  })

  return (
    <>
      <style>{bouncingDotsKeyframes}</style>
      <Group gap={3} align="center" style={{ display: 'inline-flex' }}>
        <span style={dotStyle(0)} />
        <span style={dotStyle(0.16)} />
        <span style={dotStyle(0.32)} />
      </Group>
    </>
  )
}

function formatDuration(startedAt: string) {
  const now = new Date()
  const start = new Date(startedAt)
  const diffMs = now.getTime() - start.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 60) {
    return `${diffMins} mins`
  }
  const hours = Math.floor(diffMins / 60)
  return `${hours} hours`
}
